import React from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  Icon,
  InputAdornment,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';

import styles from './styles';

const useStyles = makeStyles(styles);

const loadScript = (src, position, id) => {
  if (!position) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
};

const autocompleteService = { current: null };

export const AddressAutocompleteComponent = (props) => {
  const {
    value,
    onChange,
    label,
    required,
    error,
    helperText,
  } = props;

  const classes = useStyles();
  const [options, setOptions] = React.useState([]);
  const loaded = React.useRef(false);

  if (typeof window !== 'undefined' && !loaded.current) {
    if (!document.querySelector('#google-maps')) {
      loadScript(
        'https://maps.googleapis.com/maps/api/js?key=AIzaSyAAvKRVisK_1qDFuBX8dhiaxKQPalMe-X0&libraries=places',
        document.querySelector('head'),
        'google-maps',
      );
    }

    loaded.current = true;
  }

  const fetch = React.useMemo(
    () => throttle((input, callback) => {
      autocompleteService.current.getPlacePredictions(input, callback);
    }, 200),
    [],
  );

  React.useEffect(() => {
    let active = true;
    if (!autocompleteService.current && window.google) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (value === '') {
      setOptions([]);
      return undefined;
    }

    fetch({ input: value }, (results) => {
      if (active) {
        setOptions(results || []);
      }
    });

    return () => {
      active = false;
    };
  }, [value, fetch]);

  return (
    <Autocomplete
      id="google-map-address-autocomplete"
      getOptionLabel={(option) => (typeof option === 'string'
        ? option
        : option.description)}
      filterOptions={(x) => x}
      options={options}
      includeInputInList
      freeSolo
      value={value}
      onChange={(e, selectedAddress) => { if (selectedAddress) onChange({ target: { value: selectedAddress.description } }); }}
      renderInput={(params) => {
        const updatedParams = params;
        updatedParams.inputProps.autoComplete = 'new-password'; // don't want browser auto suggest
        return (
          <TextField
            {...updatedParams}
            margin="normal"
            label={label}
            required={required}
            error={error}
            helperText={helperText}
            FormHelperTextProps={{ component: 'div' }}
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <Icon>place</Icon>
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
            onChange={onChange}
          />
        );
      }}
      renderOption={(option) => {
        const matches = option.structured_formatting.main_text_matched_substrings;
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match) => [match.offset, match.offset + match.length]),
        );

        return (
          <Grid container alignItems="center">
            <Grid item>
              <LocationOnIcon className={classes.icon} />
            </Grid>
            <Grid item xs>
              {parts.map((part, index) => (
                <span
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  style={{
                    fontWeight: part.highlight
                      ? 700
                      : 400,
                  }}
                >
                  {part.text}
                </span>
              ))}

              <Typography variant="body2" color="textSecondary">
                {option.structured_formatting.secondary_text}
              </Typography>
            </Grid>
          </Grid>
        );
      }}
    />
  );
};

AddressAutocompleteComponent.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  label: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.any,
};
AddressAutocompleteComponent.defaultProps = {
  value: null,
  onChange: () => {},
  label: 'Address',
  required: false,
  error: false,
  helperText: '',
};

export default (AddressAutocompleteComponent);
